import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Bolt, CampaignOutlined, WhatsApp } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/apiService';
import StudioSelector, { StudioOption } from '../StudioSelector';
import MessagingPage from '../../pages/MessagingPage';
import WorkflowBuilder, { WorkflowMeta } from './WorkflowBuilder';
import WorkflowsPanel from './WorkflowsPanel';
import AutomaticMessages from './AutomaticMessages';
import TemplateGallery from './TemplateGallery';
import { wa } from './waTheme';
import { isOwnerRole } from '../../utils/roles';
import BranchSelect from '../BranchSelect';
import { branchLabel, useMyLocations } from '../../hooks/useMyLocations';

/**
 * Who may see WhatsApp at all: SweatSocial super admins, and a studio's owner
 * or manager once SweatSocial has enabled messaging for that studio. The
 * backend enforces the same rule on every endpoint.
 */
export function canUseWhatsApp(admin: any): boolean {
  const role = admin?.role ?? '';
  if (role === 'super_admin') return true;
  if (isOwnerRole(role) || role === 'manager') {
    return admin?.studio?.studioPermissions?.enableMessaging === true;
  }
  return false;
}

/**
 * A studio owner/manager whose studio doesn't have messaging yet — they see a
 * WhatsApp entry that points them to SweatSocial to get it turned on.
 */
export function isWhatsAppLocked(admin: any): boolean {
  const role = admin?.role ?? '';
  return (isOwnerRole(role) || role === 'manager') && !canUseWhatsApp(admin);
}

export const SWEATSOCIAL_WHATSAPP = '919606464667';

export const WhatsAppLocked: React.FC<{ studioName?: string }> = ({ studioName }) => {
  const text = `Hi SweatSocial! I'd like to enable WhatsApp messaging for ${studioName || 'my studio'}.`;
  const href = `https://wa.me/${SWEATSOCIAL_WHATSAPP}?text=${encodeURIComponent(text)}`;
  return (
    <Box sx={{ background: wa.gradient, borderRadius: 4, p: { xs: 3, md: 5 }, position: 'relative', overflow: 'hidden', textAlign: 'center' }}>
      <Box sx={{ position: 'absolute', right: -60, top: -60, width: 220, height: 220, borderRadius: '50%', bgcolor: 'rgba(37,211,102,0.12)' }} />
      <Stack spacing={2} alignItems="center" sx={{ position: 'relative', maxWidth: 520, mx: 'auto' }}>
        <Box sx={{ width: 64, height: 64, borderRadius: 3, bgcolor: 'rgba(37,211,102,0.2)', display: 'grid', placeItems: 'center' }}>
          <WhatsApp sx={{ color: wa.green, fontSize: 38 }} />
        </Box>
        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800 }}>WhatsApp messaging isn’t enabled yet</Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.8)' }}>
          Send campaigns, run automated reminders and reply to members, all from your studio’s own WhatsApp number.
          Contact SweatSocial to enable this feature for your studio.
        </Typography>
        <Button
          variant="contained" startIcon={<WhatsApp />} href={href} target="_blank" rel="noopener noreferrer"
          sx={{ bgcolor: '#fff', color: wa.dark, fontWeight: 800, borderRadius: 2, '&:hover': { bgcolor: '#f1f1f1' } }}
        >
          Contact SweatSocial
        </Button>
      </Stack>
    </Box>
  );
};

const Tile: React.FC<{ label: string; value: React.ReactNode; hint?: string }> = ({ label, value, hint }) => (
  <Box sx={{ p: 2, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', height: '100%' }}>
    <Typography sx={{ fontSize: 26, fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>{value}</Typography>
    <Typography sx={{ fontSize: 12.5, color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>{label}</Typography>
    {hint && <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{hint}</Typography>}
  </Box>
);

const WhatsAppHub: React.FC = () => {
  const { admin } = useAuth();
  const role = admin?.role ?? '';
  const isSuperAdmin = role === 'super_admin';
  const canWrite = role !== 'instructor';

  const [workspace, setWorkspace] = useState<'platform' | 'studio'>('platform');
  const [studio, setStudio] = useState<StudioOption | null>(null);
  // Under "All locations" a studio role must pick ONE location first: each
  // location has its own WhatsApp setup, and every call is pinned to it.
  const { isAllLocations, byId } = useMyLocations();
  const [location, setLocation] = useState<string>('');
  const isPlatform = isSuperAdmin && workspace === 'platform';
  const studioId: string | undefined = isSuperAdmin
    ? isPlatform ? 'platform' : studio?._id
    : isAllLocations
      ? location || undefined
      : (admin?.studio as any)?._id;
  const pickedLocation = !isSuperAdmin && isAllLocations ? byId(location) : undefined;
  const senderName = isPlatform
    ? 'SweatSocial'
    : (isSuperAdmin
        ? studio?.studioName
        : pickedLocation
          ? branchLabel(pickedLocation)
          : (admin?.studio as any)?.studioName) ?? 'Your studio';

  const [tab, setTab] = useState(0);
  const [meta, setMeta] = useState<WorkflowMeta | null>(null);
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any>(null);
  const [overview, setOverview] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [builder, setBuilder] = useState<{ open: boolean; kind?: 'campaign' | 'automation' }>({ open: false });
  // Templates are linked or created on the Setup tab of this same hub, after
  // the hub first loaded — so re-read them every time the builder opens.
  useEffect(() => {
    if (!builder.open) return;
    const forStudio = studioId;
    apiService
      .getWorkflowMeta(forStudio)
      .then((r: any) => {
        if (currentStudio.current === forStudio) setMeta(r?.data ?? r);
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builder.open]);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);
  const notify = (msg: string, sev: 'success' | 'error' = 'success') => setToast({ msg, sev });

  // Responses for a workspace the user already left must never be shown.
  const currentStudio = useRef<string | undefined>(studioId);
  currentStudio.current = studioId;
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const load = useCallback(async () => {
    const forStudio = studioId;
    if (!forStudio) return;
    setLoading(true);
    // Each part loads independently — one failing call doesn't blank the hub.
    const [m, w, t, o] = await Promise.allSettled([
      apiService.getWorkflowMeta(forStudio),
      apiService.getWorkflows(undefined, forStudio),
      apiService.getMessagingTemplates(forStudio),
      apiService.getMessagingOverview(forStudio),
    ]);
    if (currentStudio.current !== forStudio) return;
    const val = (r: PromiseSettledResult<any>) => (r.status === 'fulfilled' ? (r.value?.data ?? r.value) : undefined);
    if (m.status === 'fulfilled') setMeta(val(m));
    if (w.status === 'fulfilled') setWorkflows(val(w)?.workflows ?? []);
    if (t.status === 'fulfilled') setTemplates(val(t));
    if (o.status === 'fulfilled') setOverview(val(o));
    const failed = [m, w, t, o].find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined;
    if (failed) notify(failed.reason?.message ?? 'Some WhatsApp data could not be loaded', 'error');
    setLoading(false);
  }, [studioId]);

  useEffect(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setMeta(null);
    setWorkflows([]);
    setTemplates(null);
    setOverview(null);
    load();
    return () => timers.current.forEach(clearTimeout);
  }, [load]);

  // A "send now" campaign runs in the background — refresh a few times.
  const refreshSoon = () => {
    load();
    timers.current.push(...[4000, 10000, 20000].map((ms) => setTimeout(load, ms)));
  };

  const wa7d = overview?.byChannel?.whatsapp ?? {};
  const sent7d = (wa7d.sent ?? 0) + (wa7d.delivered ?? 0) + (wa7d.read ?? 0);
  const delivered7d = (wa7d.delivered ?? 0) + (wa7d.read ?? 0);
  const reached7d = sent7d + (wa7d.failed ?? 0);
  const rate7d = reached7d ? Math.round((delivered7d / reached7d) * 100) : 0;
  const activeAutomations = workflows.filter((w) => w.kind === 'automation' && w.status === 'active').length;
  const approved = templates?.summary?.approved ?? 0;

  const locationPicker = !isSuperAdmin && isAllLocations && (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} mb={2}>
      <Box sx={{ minWidth: 320 }}>
        <BranchSelect value={location} onChange={(v) => setLocation(v || '')} minRole="manager" />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
        Each location has its own WhatsApp number and setup.
      </Typography>
    </Stack>
  );

  const picker = isSuperAdmin ? (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} mb={2}>
      <ToggleButtonGroup
        size="small" exclusive value={workspace} onChange={(_, v) => v && setWorkspace(v)}
        sx={{
          bgcolor: '#fff',
          '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, px: 2 },
          '& .Mui-selected': { bgcolor: `${wa.tint} !important`, color: `${wa.dark} !important` },
        }}
      >
        <ToggleButton value="platform">SweatSocial’s number</ToggleButton>
        <ToggleButton value="studio">A studio’s number</ToggleButton>
      </ToggleButtonGroup>
      {workspace === 'studio' && (
        <Box sx={{ minWidth: 320 }}>
          <StudioSelector value={studio} onChange={setStudio} />
        </Box>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
        {isPlatform
          ? 'Only SweatSocial app users ever hear from SweatSocial’s number.'
          : 'Studios message their own members from their own WhatsApp number.'}
      </Typography>
    </Stack>
  ) : locationPicker;

  if (!studioId) {
    return (
      <Box>
        {picker}
        <Alert severity="info">
          {isSuperAdmin ? 'Pick a studio to manage its WhatsApp.' : 'Pick a location to manage its WhatsApp.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {picker}

      {/* Header */}
      <Box sx={{ background: wa.gradient, borderRadius: 4, p: { xs: 2.5, md: 3 }, mb: 3, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', right: -60, top: -60, width: 220, height: 220, borderRadius: '50%', bgcolor: 'rgba(37,211,102,0.12)' }} />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ position: 'relative' }}>
          <Stack direction="row" spacing={2} alignItems="center" flexGrow={1}>
            <Box sx={{ width: 54, height: 54, borderRadius: 3, bgcolor: 'rgba(37,211,102,0.2)', display: 'grid', placeItems: 'center' }}>
              <WhatsApp sx={{ color: wa.green, fontSize: 32 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800 }}>WhatsApp · {senderName}</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Campaigns, daily automations, templates and replies — all in one place
              </Typography>
            </Box>
          </Stack>
          {canWrite && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained" startIcon={<CampaignOutlined />} onClick={() => setBuilder({ open: true, kind: 'campaign' })}
                sx={{ bgcolor: '#fff', color: wa.dark, fontWeight: 800, borderRadius: 2, '&:hover': { bgcolor: '#f1f1f1' } }}
              >
                New campaign
              </Button>
              <Button
                variant="outlined" startIcon={<Bolt />} onClick={() => setBuilder({ open: true, kind: 'automation' })}
                sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', fontWeight: 800, borderRadius: 2, '&:hover': { borderColor: '#fff' } }}
              >
                New automation
              </Button>
            </Stack>
          )}
        </Stack>
        <Grid container spacing={1.5} sx={{ mt: 2, position: 'relative' }}>
          <Grid size={{ xs: 6, md: 3 }}><Tile label="Sent · last 7 days" value={sent7d} /></Grid>
          <Grid size={{ xs: 6, md: 3 }}><Tile label="Delivered" value={`${rate7d}%`} hint={`${delivered7d} delivered or read`} /></Grid>
          <Grid size={{ xs: 6, md: 3 }}><Tile label="Automations running" value={activeAutomations} /></Grid>
          <Grid size={{ xs: 6, md: 3 }}><Tile label="Approved templates" value={approved} /></Grid>
        </Grid>
      </Box>

      <Paper elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.07)', borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: '1px solid rgba(0,0,0,0.07)', bgcolor: '#fff', px: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            sx={{
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, minHeight: 52 },
              '& .Mui-selected': { color: `${wa.teal} !important` },
              '& .MuiTabs-indicator': { bgcolor: wa.teal, height: 3 },
            }}
          >
            <Tab label="Workflows" />
            <Tab label="Templates" />
            <Tab label="Inbox & delivery" />
            <Tab label="Setup" />
          </Tabs>
        </Box>
        <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#fafafa' }}>
          {tab === 0 && <AutomaticMessages key={`auto-${studioId}`} studioId={studioId} canWrite={canWrite} notify={notify} />}
          {tab === 0 && (
            <WorkflowsPanel
              workflows={workflows}
              loading={loading && !workflows.length}
              meta={meta}
              studioId={studioId}
              canWrite={canWrite}
              onChanged={load}
              onNew={(kind) => setBuilder({ open: true, kind })}
              notify={notify}
            />
          )}
          {tab === 1 && (
            <TemplateGallery
              templates={templates}
              labels={meta?.autoVariables ?? {}}
              studioId={studioId}
              canWrite={canWrite}
              onManage={() => setTab(3)}
              onRefreshed={load}
              notify={notify}
            />
          )}
          {tab === 2 && (
            <MessagingPage key={`inbox-${studioId}`} embedded={{ studioId, isPlatform, tabs: [6, 4, 5] }} />
          )}
          {tab === 3 && (
            <MessagingPage key={`setup-${studioId}`} embedded={{ studioId, isPlatform, tabs: [2, 3, 1] }} />
          )}
        </Box>
      </Paper>

      <WorkflowBuilder
        open={builder.open}
        initialKind={builder.kind}
        onClose={() => setBuilder({ open: false })}
        onCreated={(msg) => {
          setBuilder({ open: false });
          setTab(0);
          notify(msg);
          refreshSoon();
        }}
        studioId={studioId}
        senderName={senderName}
        isPlatform={isPlatform}
        meta={meta}
      />

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={toast?.sev ?? 'success'} variant="filled" onClose={() => setToast(null)}>{toast?.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default WhatsAppHub;
